import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BLOG_POSTS, getPost } from "@/data/blog";
import { formatDate } from "@/lib/api";
import { useSeo, breadcrumb, absolute, AUTEUR } from "@/lib/seo";
import { api } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";

const Bloc = ({ b }) => {
  switch (b.type) {
    case "h2": return <h2 className="font-serif text-3xl text-bark mt-14 mb-5 leading-tight">{b.texte}</h2>;
    case "h3": return <h3 className="font-serif text-2xl text-bark mt-10 mb-3">{b.texte}</h3>;
    case "p": return <p className="text-bark/85 leading-[1.8] mb-5">{b.texte}</p>;
    case "list": return (
      <ul className="space-y-3 mb-6">
        {b.items.map((it, i) => <li key={i} className="flex gap-4 text-bark/85 leading-relaxed"><span className="font-mono text-xs text-terra mt-1.5">0{i + 1}</span><span>{it}</span></li>)}
      </ul>
    );
    case "quote": return (
      <blockquote className="my-12 border-l-2 border-terra/50 pl-8">
        <p className="font-serif text-2xl lg:text-3xl text-bark leading-relaxed italic">« {b.texte} »</p>
      </blockquote>
    );
    case "image": return <figure className="my-10 rounded-2xl overflow-hidden"><img src={b.src} alt={b.alt || ""} loading="lazy" className="w-full object-cover" /></figure>;
    case "gallery": return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-10">
        {b.images.map((src, i) => <div key={i} className="aspect-square rounded-xl overflow-hidden bg-sand-dark"><img src={src} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" /></div>)}
      </div>
    );
    case "cta": return (
      <div className="mt-12">
        <Link to={b.to} data-testid="blog-cta" className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-earth text-sand text-sm hover:bg-terra hover:text-earth transition-colors duration-300">
          {b.texte} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
    default: return null;
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const { locale } = useLocale();
  const sourcePost = getPost(slug);
  const [post, setPost] = useState(sourcePost);
  useEffect(() => {
    setPost(sourcePost);
    if (locale !== "en" || !sourcePost) return;
    api.articleTranslations([sourcePost.slug]).then((translations) => setPost((current) => current?.slug === sourcePost.slug ? { ...sourcePost, ...(translations[sourcePost.slug] || {}) } : current)).catch(() => {});
  }, [locale, slug]);

  useSeo({
    title: post ? `${post.titre} — ${locale === "en" ? "Journal" : "Blog"} · Entre les Mondes` : null,
    description: post?.accroche || "",
    image: post?.cover,
    type: "article",
    jsonLd: post ? [breadcrumb([[locale === "en" ? "Home" : "Accueil", "/"], [locale === "en" ? "Journal" : "Blog", "/blog"], [post.titre, `/blog/${post.slug}`]]), { "@type": "Article", headline: post.titre, description: post.accroche, image: post.cover, datePublished: post.date, dateModified: post.date, articleSection: post.categorie, inLanguage: locale === "en" ? "en-CH" : "fr-CH", author: AUTEUR, publisher: { "@type": "Organization", name: "Entre Les Mondes", logo: { "@type": "ImageObject", url: absolute("/logo.png") } }, mainEntityOfPage: absolute(`/blog/${post.slug}`) }] : null,
  });

  if (!post) {
    return (
      <div className="pt-44 pb-32 text-center">
        <p className="font-serif text-3xl text-bark mb-6">{locale === "en" ? "This article has drifted away like sage smoke." : "Cet article s'est évaporé comme la fumée de sauge."}</p>
        <Link to="/blog" data-testid="blogpost-notfound-back" className="link-line text-terra">{locale === "en" ? "Back to the journal" : "Retour au blog"}</Link>
      </div>
    );
  }

  const suivants = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <article data-testid="blog-post">
      <section className="relative bg-earth grain text-sand overflow-hidden min-h-[70vh] flex items-end">
        <img src={post.cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-earth via-earth/50 to-earth/20" />
        <div className="relative z-[2] w-full max-w-4xl mx-auto px-6 pb-20 pt-44">
          <Link to="/blog" data-testid="blogpost-back" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-sand/60 hover:text-sand transition-colors mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> {locale === "en" ? "Journal" : "Le blog"}
          </Link>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra mb-4">{post.categorie}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="blogpost-title">{post.titre}</h1>
          <p className="mt-6 text-sand/70 max-w-2xl leading-relaxed">{post.accroche}</p>
          <p className="font-mono text-xs text-sand/60 mt-6 flex items-center gap-3 capitalize">{formatDate(post.date, { weekday: undefined, locale: locale === "en" ? "en-CH" : "fr-CH" })} <span>·</span> <Clock className="w-3.5 h-3.5" /> {post.lecture} {locale === "en" ? "read" : "de lecture"} <span>·</span> Ludovic Isoz</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-20 lg:py-28">
        <Reveal>
          {post.blocs.map((b, i) => <Bloc key={i} b={b} />)}
        </Reveal>
      </section>

      <section className="bg-sand-dark/50 border-y border-sand-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <Reveal className="flex items-end justify-between gap-6 mb-10">
            <h2 className="font-serif text-3xl text-bark">{locale === "en" ? "Keep reading" : "Continuer la lecture"}</h2>
            <Link to="/blog" data-testid="blogpost-all" className="link-line text-sm text-fumee hover:text-bark inline-flex items-center gap-2">{locale === "en" ? "All articles" : "Tous les articles"} <ArrowRight className="w-4 h-4" /></Link>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {suivants.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blogpost-next-${p.slug}`} className="group block">
                <div className="rounded-2xl overflow-hidden aspect-[16/10] mb-5"><img src={p.cover} alt={p.titre} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" /></div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra mb-2">{p.categorie}</p>
                <h3 className="font-serif text-xl text-bark leading-snug group-hover:text-terra transition-colors">{p.titre}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
};

export default BlogPost;
